import { useState, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    DollarSign, Calendar, BarChart3, CalendarDays, ChevronDown,
    TrendingUp, TrendingDown, Wallet, FileText,
} from 'lucide-react';
import { ExportDropdown } from '@components/ui';
import { FinancialReportSkeleton } from '@components/skeleton';
import { useExportExcel, useExportPDF } from '@hooks';
import clsx from 'clsx';
import {
    getFinancialSummaryByConstructionId,
    getReceivablesSummary,
    getPayablesSummary
} from '@mocks/maliOzet';

// Tab Components
import {
    IstatistikKartlari,
    GenelBakis,
    GelirAnalizi,
    GiderAnalizi,
    NakitAkisi,
    IslemGecmisi,
    AylikDetay,
} from './components';

// ─── TAB CONFIG ───────────────────────────────
const tabs = [
    { id: 'overview', label: 'Genel Bakış', icon: BarChart3 },
    { id: 'income', label: 'Gelir Analizi', icon: TrendingUp },
    { id: 'expense', label: 'Gider Analizi', icon: TrendingDown },
    { id: 'cashflow', label: 'Nakit Akışı', icon: Wallet },
    { id: 'transactions', label: 'İşlem Geçmişi', icon: FileText },
    { id: 'monthly', label: 'Aylık Detay', icon: CalendarDays },
];

export default function FinancialReport() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const constructionId = showAllConstructions ? null : selectedConstruction?.id;

    // Active tab
    const [activeTab, setActiveTab] = useState('overview');

    // Year selection
    const allData = getFinancialSummaryByConstructionId(null);
    const availableYears = allData
        ? [...new Set(allData.monthlyBreakdown.map(m => m.month.split('-')[0]))].sort().reverse()
        : ['2026'];
    const [selectedYear, setSelectedYear] = useState(availableYears[0]);
    const [showYearDropdown, setShowYearDropdown] = useState(false);

    // Data
    const summary = getFinancialSummaryByConstructionId(constructionId, selectedYear);
    const receivables = getReceivablesSummary(constructionId);
    const payables = getPayablesSummary(constructionId);

    // All transactions (deduplicated from monthly + recent)
    const allTransactions = useMemo(() => {
        if (!summary) return [];
        const txMap = new Map();
        (summary.monthlyBreakdown || []).forEach(m => (m.transactions || []).forEach(t => txMap.set(t.id, t)));
        (summary.recentTransactions || []).forEach(t => txMap.set(t.id, t));
        return Array.from(txMap.values()).sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [summary]);

    // Export
    const reportRef = useRef(null);
    const { exportToExcel, isExporting: isExportingExcel } = useExportExcel();
    const { exportToPDF, isExporting: isExportingPDF } = useExportPDF();

    // ─── FORMATTERS ───────────────────────────
    const formatCurrency = (amount) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(amount);
    const formatCompact = (amount) => {
        if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M ₺`;
        if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K ₺`;
        return formatCurrency(amount);
    };
    const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });

    // ─── EXPORT HANDLERS ──────────────────────
    const handleExportExcel = () => {
        const monthlySheet = (summary?.monthlyBreakdown || []).map(m => ({
            Ay: m.monthLabel, Gelir: m.income, Gider: m.expense, Net: m.income - m.expense,
        }));
        exportToExcel({
            data: monthlySheet,
            columns: [
                { header: 'Ay', key: 'Ay' },
                { header: 'Gelir', key: 'Gelir', format: 'currency' },
                { header: 'Gider', key: 'Gider', format: 'currency' },
                { header: 'Net', key: 'Net', format: 'currency' },
            ],
            fileName: `mali_rapor_${selectedYear}`,
            sheetName: 'Aylık Özet',
        });
    };

    const handleExportPDF = () => exportToPDF({
        elementRef: reportRef, fileName: `mali_rapor_${selectedYear}`,
        title: `Mali Özet Raporu - ${selectedYear}`, orientation: 'portrait',
    });

    // ─── LOADING / EMPTY ──────────────────────
    const [isLoading] = useState(false);
    if (isLoading) return <FinancialReportSkeleton />;
    if (!summary) return (
        <div className="p-6 text-center py-12 text-text-tertiary">
            <DollarSign size={48} className="mx-auto mb-4 opacity-50" />
            <p>Mali özet bulunamadı.</p>
        </div>
    );

    // ─── RENDER ───────────────────────────────
    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-text-primary">Mali Rapor</h1>
                        <p className="text-text-tertiary text-sm mt-1">
                            {showAllConstructions ? 'Tüm İnşaatlar' : selectedConstruction?.name} • {selectedYear} Yılı
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Year Selector */}
                        <div className="relative">
                            <button onClick={() => setShowYearDropdown(!showYearDropdown)}
                                className="flex items-center gap-2 text-sm text-text-secondary bg-bg-tertiary px-4 py-2 rounded-lg hover:bg-bg-hover transition-all font-medium border border-border-light">
                                <Calendar size={16} />
                                <span>{selectedYear}</span>
                                <ChevronDown size={14} className={`transition-transform ${showYearDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            {showYearDropdown && (
                                <div className="absolute top-full right-0 mt-2 w-28 bg-bg-secondary rounded-lg shadow-xl border border-border-light overflow-hidden z-50">
                                    {availableYears.map(y => (
                                        <button key={y} onClick={() => { setSelectedYear(y); setShowYearDropdown(false); }}
                                            className={`w-full px-4 py-2 text-sm text-left hover:bg-bg-hover ${selectedYear === y ? 'text-primary font-semibold bg-primary/5' : 'text-text-secondary'}`}>
                                            {y}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <ExportDropdown onExportExcel={handleExportExcel} onExportPDF={handleExportPDF}
                            isExporting={isExportingExcel || isExportingPDF} />
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="flex gap-1.5 overflow-x-auto pb-1 border-b border-border-light -mb-px">
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;
                        return (
                            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                                className={clsx(
                                    "flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-t-lg transition-all border-b-2",
                                    isActive
                                        ? "border-primary text-primary bg-primary/5"
                                        : "border-transparent text-text-muted hover:text-text-secondary hover:bg-bg-hover"
                                )}>
                                <Icon size={16} />
                                <span className="hidden sm:inline">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Stat Cards (always visible) */}
            <IstatistikKartlari summary={summary} formatCurrency={formatCurrency} />

            {/* Tab Content */}
            <div ref={reportRef}>
                {activeTab === 'overview' && (
                    <GenelBakis summary={summary} receivables={receivables} payables={payables}
                        formatCurrency={formatCurrency} formatCompact={formatCompact} formatDate={formatDate} />
                )}
                {activeTab === 'income' && (
                    <GelirAnalizi summary={summary} receivables={receivables}
                        formatCurrency={formatCurrency} formatDate={formatDate} />
                )}
                {activeTab === 'expense' && (
                    <GiderAnalizi summary={summary} payables={payables} allTransactions={allTransactions}
                        formatCurrency={formatCurrency} formatDate={formatDate} />
                )}
                {activeTab === 'cashflow' && (
                    <NakitAkisi summary={summary} formatCurrency={formatCurrency}
                        formatCompactCurrency={formatCompact} formatDate={formatDate} />
                )}
                {activeTab === 'transactions' && (
                    <IslemGecmisi allTransactions={allTransactions}
                        formatCurrency={formatCurrency} formatDate={formatDate} />
                )}
                {activeTab === 'monthly' && (
                    <AylikDetay monthlyData={summary.monthlyBreakdown}
                        selectedYear={selectedYear} formatCurrency={formatCurrency} />
                )}
            </div>
        </div>
    );
}
